import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaUsers, FaChartBar, FaSignOutAlt, FaUser } from 'react-icons/fa';
import styled from 'styled-components';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
`;

const Sidebar = styled.div`
  width: 250px;
  background: linear-gradient(180deg, #2c3e50 0%, #34495e 100%);
  color: white;
  padding: 20px 0;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
`;

const Logo = styled.div`
  text-align: center;
  padding: 20px;
  border-bottom: 1px solid #34495e;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  
  img {
    height: 40px;
    width: auto;
  }
  
  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }
`;

const NavMenu = styled.nav`
  padding: 0 20px;
`;

const NavItem = styled.div`
  margin-bottom: 10px;
`;

const NavLink = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 15px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: ${props => props.$active ? '#fff' : '#bdc3c7'};
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
  
  svg {
    margin-right: 12px;
    font-size: 18px;
  }
`;

const MainContent = styled.div`
  flex: 1;
  background: #f8f9fa;
`;

const Header = styled.header`
  background: white;
  padding: 15px 30px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 12px 15px;
    flex-wrap: wrap;
    gap: 10px;
    
    h1 {
      font-size: 1.2rem !important;
    }
  }
  
  @media (max-width: 480px) {
    padding: 10px 12px;
    
    h1 {
      font-size: 1rem !important;
    }
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  @media (max-width: 768px) {
    gap: 8px;
    flex-wrap: wrap;
  }
  
  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const UserName = styled.span`
  font-weight: 500;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    display: none;
  }
`;

const LogoutButton = styled.button`
  background: none;
  border: none;
  color: #e74c3c;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  border-radius: 5px;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  white-space: nowrap;
  
  &:hover {
    background: #fdf2f2;
  }
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 0.85rem;
    gap: 4px;
    
    svg {
      font-size: 14px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 5px 8px;
    font-size: 0.75rem;
    
    svg {
      font-size: 16px;
      margin: 0;
    }
    
    /* Esconde o texto "Sair" em mobile, mantém apenas o ícone */
    font-size: 0;
    line-height: 0;
    
    svg {
      font-size: 18px;
    }
  }
`;

const Content = styled.main`
  padding: 30px;
`;

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: <FaChartBar /> },
    { path: '/clientes', label: 'Clientes', icon: <FaUsers /> },
  ];

  return (
    <LayoutContainer>
      <Sidebar>
        <Logo>
          <img src="/Logo.ico" alt="Veritux ERP" style={{ height: '40px', width: 'auto', marginBottom: '10px' }} />
          <h2 style={{ margin: 0, fontSize: '18px' }}>Veritux ERP</h2>
        </Logo>
        
        <NavMenu>
          {navItems.map((item) => (
            <NavItem key={item.path}>
              <NavLink
                $active={location.pathname === item.path}
                onClick={() => navigate(item.path)}
              >
                {item.icon}
                {item.label}
              </NavLink>
            </NavItem>
          ))}
        </NavMenu>
      </Sidebar>
      
      <MainContent>
        <Header>
          <div style={{ flex: '1', minWidth: 0, overflow: 'hidden' }}>
            <h1 style={{ margin: 0, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {location.pathname === '/dashboard' && 'Dashboard'}
              {location.pathname === '/clientes' && 'Gestão de Clientes'}
              {location.pathname === '/clients' && 'Gestão de Clientes'}
              {location.pathname.startsWith('/clientes/') && 'Detalhes do Cliente'}
              {location.pathname.startsWith('/clients/') && 'Detalhes do Cliente'}
              {location.pathname === '/clientes/novo' && 'Nova Empresa'}
            </h1>
          </div>
          
          <UserInfo>
            <FaUser style={{ flexShrink: 0 }} />
            <UserName>{user?.name || user?.email || 'Usuário'}</UserName>
            <LogoutButton onClick={handleLogout} aria-label="Sair">
              <FaSignOutAlt />
              Sair
            </LogoutButton>
          </UserInfo>
        </Header>
        
        <Content>
          {children}
        </Content>
      </MainContent>
    </LayoutContainer>
  );
};

export default Layout; 